import React, { useState } from "react";
import { Check, Heart, Banknote, CalendarCheck, Users, ArrowRight, Upload, FileText, X, Copy, MessageCircle, Facebook, Mail, Share2 } from "lucide-react";
import Seo from "../components/Seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { cities, cityLabel, IMAGES } from "../mock";
import { useLang } from "../context/LanguageContext";
import { createApplication } from "../api";

const iconMap = { Banknote, CalendarCheck, Users, Heart };

const Jobs = () => {
  const { lang, t } = useLang();
  const j = t.jobs;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", experience: "", availability: "" });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = lang === "de" ? "https://mycleaner.ch/putzfrau-werden" : "https://mycleaner.ch/en/putzfrau-werden";
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(j.shareText); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* noop */ }
  };
  const waHref = `https://wa.me/?text=${encodeURIComponent(j.shareText)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(lang === "de" ? "Job als Putzfrau bei MyCleaner.ch" : "Cleaning job at MyCleaner.ch")}&body=${encodeURIComponent(j.shareText)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast({ title: j.err, variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      await createApplication({ ...form, cv });
      toast({ title: j.okTitle, description: j.okDesc });
      setForm({ name: "", email: "", phone: "", city: "", experience: "", availability: "" });
      setCv(null);
    } catch (err) {
      toast({ title: j.err, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const seo = lang === "de"
    ? { title: "Putzfrau gesucht? Jetzt als Putzfrau bewerben | MyCleaner.ch", description: "Wir suchen erfahrene Putzfrauen in der ganzen Schweiz. Faire Bezahlung, flexible Einteilung und treue Kundschaft in Zürich, Zug, Luzern, Baden, Genf und mehr. Jetzt bewerben!", keywords: "Putzfrau gesucht, Putzfrau Job, Putzstelle, Putzfrau bewerben Schweiz, Haushaltshilfe Job, Reinigungskraft gesucht, Putzfrau werden" }
    : { title: "Looking for cleaning work? Apply as a cleaner | MyCleaner.ch", description: "We're hiring experienced cleaners across Switzerland. Fair pay, flexible scheduling and loyal clients in Zurich, Zug, Lucerne, Baden, Geneva and more. Apply now!", keywords: "cleaning job, cleaner job Switzerland, apply as cleaner, home help job, cleaning position, become a cleaner" };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (j.faqs || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <div>
      <Seo pageKey="jobs" title={seo.title} description={seo.description} keywords={seo.keywords} jsonLd={faqJsonLd} />

      <section className="pt-[116px] pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-teal-600 font-semibold text-sm tracking-wide uppercase mb-3">{j.eyebrow}</p>
            <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-tight text-slate-900 mb-5">{j.title}</h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{j.subtitle}</p>
            <ul className="space-y-3">
              {j.reqs.map((li) => (
                <li key={li} className="flex items-center gap-3 text-slate-700"><span className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>{li}</li>
              ))}
            </ul>
            <p className="text-sm text-slate-500 mt-5">{j.citiesLine}</p>
            <span className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-teal-50 text-teal-800 text-sm font-semibold border border-teal-100" data-testid="jobs-free-badge">
              <Check className="w-4 h-4" /> {j.freeBadge}
            </span>
            <div className="mt-4">
              <a href={lang === "de" ? "/putzfrau-portal" : "/en/putzfrau-portal"} data-testid="jobs-portal-link" className="text-sm text-teal-700 font-semibold hover:underline">Schon registriert & freigegeben? → Zum Putzfrau-Portal (Login)</a>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img src={IMAGES.about} alt={lang === "de" ? "Freundliche professionelle Putzfrau bei MyCleaner.ch" : "Friendly professional cleaner at MyCleaner.ch"} className="w-full h-[400px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {j.benefits.map((b) => {
            const Icon = iconMap[b.icon];
            return (
              <div key={b.title} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-teal-700" /></div>
                <h3 className="font-head font-bold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <div className="rounded-3xl bg-teal-700 text-white p-8 md:p-10">
            <div className="flex items-center gap-2 text-teal-100 text-sm font-semibold mb-3"><Share2 className="w-4 h-4" /> Teilen & empfehlen</div>
            <h2 className="font-head font-extrabold text-2xl md:text-3xl mb-2">{j.shareTitle}</h2>
            <p className="text-teal-100 leading-relaxed mb-6">{j.shareSub}</p>
            <div data-testid="jobs-share-text" className="bg-white/10 rounded-2xl p-5 text-teal-50 text-sm leading-relaxed whitespace-pre-line mb-5">{j.shareText}</div>
            <div className="flex flex-wrap gap-3">
              <button data-testid="jobs-share-copy" onClick={copyShare} className="inline-flex items-center gap-2 bg-white text-teal-800 font-semibold px-5 py-3 rounded-xl hover:bg-teal-50 transition-colors">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? j.copied : j.copyBtn}
              </button>
              <a data-testid="jobs-share-whatsapp" href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold px-5 py-3 rounded-xl hover:bg-teal-500 transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
              <a data-testid="jobs-share-facebook" href={fbHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold px-5 py-3 rounded-xl hover:bg-teal-500 transition-colors"><Facebook className="w-4 h-4" /> Facebook</a>
              <a data-testid="jobs-share-email" href={mailHref} className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold px-5 py-3 rounded-xl hover:bg-teal-500 transition-colors"><Mail className="w-4 h-4" /> E-Mail</a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-slate-100">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <p className="text-slate-600 leading-relaxed mb-10">{j.intro}</p>
          <h2 className="font-head font-extrabold text-2xl md:text-3xl text-slate-900 mb-8">{j.sectionsTitle}</h2>
          <div className="space-y-8">
            {(j.sections || []).map((s) => (
              <div key={s.h} data-testid={`jobs-section-${s.h.slice(0, 12)}`}>
                <h3 className="font-head font-bold text-lg text-slate-900 mb-2">{s.h}</h3>
                <p className="text-slate-600 leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-100">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <h2 className="font-head font-extrabold text-2xl md:text-3xl text-slate-900 mb-8">{j.faqTitle}</h2>
          <div className="space-y-4">
            {(j.faqs || []).map((f) => (
              <details key={f.q} data-testid={`jobs-faq-${f.q.slice(0, 12)}`} className="group bg-white rounded-2xl border border-slate-100 p-5">
                <summary className="font-head font-bold text-slate-900 cursor-pointer list-none flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-teal-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-slate-600 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-amber-50/50">
        <div className="max-w-[640px] mx-auto px-5 md:px-6">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
            <div className="bg-teal-700 px-6 py-5 text-white"><h2 className="font-head font-bold text-xl">{j.formTitle}</h2><p className="text-teal-100 text-sm mt-1">{j.formSub}</p></div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder={j.fName} className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} type="email" placeholder={j.fEmail} className="h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
                <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder={j.fPhone} className="h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
              </div>
              <Select value={form.city} onValueChange={(v)=>setForm({...form,city:v})}>
                <SelectTrigger className="h-12 rounded-xl border-slate-200"><SelectValue placeholder={j.fCity} /></SelectTrigger>
                <SelectContent>{cities.map((c)=>(<SelectItem key={c.slug} value={c.slug}>{cityLabel(c, lang)}</SelectItem>))}</SelectContent>
              </Select>
              <Select value={form.availability} onValueChange={(v)=>setForm({...form,availability:v})}>
                <SelectTrigger data-testid="jobs-availability" className="h-12 rounded-xl border-slate-200"><SelectValue placeholder={j.fAvailability} /></SelectTrigger>
                <SelectContent>{j.availOptions.map((o)=>(<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
              </Select>
              <textarea value={form.experience} onChange={(e)=>setForm({...form,experience:e.target.value})} rows={4} placeholder={j.fExp} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 resize-none" />
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">{j.fCv}</label>
                {!cv ? (
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl px-4 py-7 cursor-pointer hover:border-teal-600 hover:bg-teal-50/40 transition-colors">
                    <Upload className="w-6 h-6 text-teal-700" />
                    <span className="text-sm font-medium text-slate-700">{j.fCv}</span>
                    <span className="text-xs text-slate-400">{j.cvHint}</span>
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files?.[0] || null)} />
                  </label>
                ) : (
                  <div className="flex items-center justify-between gap-3 border border-teal-200 bg-teal-50 rounded-xl px-4 py-3">
                    <span className="flex items-center gap-2 text-sm text-teal-800 truncate"><FileText className="w-4 h-4 shrink-0" /> <span className="truncate">{cv.name}</span></span>
                    <button type="button" onClick={() => setCv(null)} className="text-slate-500 hover:text-red-500 shrink-0"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <button type="submit" disabled={submitting} className="w-full h-14 rounded-xl bg-teal-700 text-white font-head font-bold text-lg hover:bg-teal-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{submitting ? "…" : j.submit} <ArrowRight className="w-5 h-5" /></button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Jobs;
